import { Box } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";

/**
 * A stylized mini argument-diagram standing in for the product: canvas
 * style nodes (rounded rects, colored borders) joined by directed
 * arrows, echoing the app's actual canvas. The hero's rotating word
 * highlights its node — supports lead to "thinking" at the top, so the
 * visual doubles as a small, honest argument map.
 *
 * Pure SVG with a fixed viewBox; the container scales it, so layout
 * coordinates below are design-space pixels, not screen pixels.
 */

type HeroDiagramProps = {
  activeWord: string;
  activeColor: string;
};

type NodeDef = {
  word: string;
  cx: number;
  cy: number;
  w: number;
  h: number;
};

const NODE_H = 46;

const nodes: NodeDef[] = [
  { word: "thinking", cx: 240, cy: 56, w: 118, h: NODE_H },
  { word: "annotation", cx: 116, cy: 168, w: 136, h: NODE_H },
  { word: "diagramming", cx: 364, cy: 168, w: 148, h: NODE_H },
  { word: "writing", cx: 96, cy: 284, w: 106, h: NODE_H },
  { word: "collaboration", cx: 366, cy: 284, w: 158, h: NODE_H },
];

// Directed support edges, source → target ("X supports Y").
const edges: Array<[string, string]> = [
  ["annotation", "thinking"],
  ["diagramming", "thinking"],
  ["writing", "annotation"],
  ["collaboration", "diagramming"],
];

const nodeByWord = new Map(nodes.map((n) => [n.word, n]));

// Point where the ray from a node's center toward `towards` crosses
// the node's rectangular border, pushed out by `gap` so arrows don't
// touch the stroke.
const rectBoundaryPoint = (
  node: NodeDef,
  towards: { cx: number; cy: number },
  gap: number
) => {
  const dx = towards.cx - node.cx;
  const dy = towards.cy - node.cy;
  const t = Math.min(
    dx !== 0 ? node.w / 2 / Math.abs(dx) : Infinity,
    dy !== 0 ? node.h / 2 / Math.abs(dy) : Infinity
  );
  const len = Math.hypot(dx, dy);
  const scale = t + gap / len;
  return { x: node.cx + dx * scale, y: node.cy + dy * scale };
};

const ARROWHEAD = 9;

// The word at the diagram's root — every support chain ends here, so
// its turn in the rotation gets the crescendo treatment below.
const CONCLUSION = "thinking";

export const HeroDiagram = ({ activeWord, activeColor }: HeroDiagramProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const mutedStroke = alpha(theme.palette.text.secondary, isDark ? 0.5 : 0.55);
  const nodeFill = alpha(theme.palette.background.paper, isDark ? 0.55 : 0.8);
  const textFill = theme.palette.text.primary;
  // When the conclusion is active the whole argument brightens with
  // it: non-incident edges rise from muted toward full strength, so
  // the moment reads as "everything flows into this" rather than one
  // more node taking its turn.
  const conclusionActive = activeWord === CONCLUSION;
  const supportingStroke = conclusionActive
    ? alpha(theme.palette.text.secondary, isDark ? 0.85 : 0.9)
    : mutedStroke;

  return (
    <Box
      sx={{
        width: { xs: "min(92vw, 400px)", sm: 460, md: "clamp(460px, 62vh, 720px)" },
        // Soft radial backdrop in place of the old circular frame —
        // keeps the glow language without boxing the diagram in.
        background: `radial-gradient(ellipse at 50% 45%, ${alpha(
          theme.palette.primary.light,
          isDark ? 0.14 : 0.16
        )} 0%, transparent 70%)`,
      }}
    >
      <svg
        viewBox="0 0 480 336"
        width="100%"
        role="img"
        aria-label="A small argument diagram: annotation, diagramming, writing, and collaboration supporting thinking"
      >
        {edges.map(([fromWord, toWord]) => {
          const from = nodeByWord.get(fromWord)!;
          const to = nodeByWord.get(toWord)!;
          const active = activeWord === fromWord || activeWord === toWord;
          const stroke = active ? activeColor : supportingStroke;

          const p1 = rectBoundaryPoint(from, to, 4);
          const p2 = rectBoundaryPoint(to, from, 4);
          // Pull the line back so it ends at the arrowhead's base.
          const dx = p2.x - p1.x;
          const dy = p2.y - p1.y;
          const len = Math.hypot(dx, dy);
          const ux = dx / len;
          const uy = dy / len;
          const bx = p2.x - ux * ARROWHEAD;
          const by = p2.y - uy * ARROWHEAD;
          // Arrowhead as an explicit polygon: markers can't take a
          // per-edge color without one <marker> per color.
          const wing = ARROWHEAD * 0.55;
          const head = [
            `${p2.x},${p2.y}`,
            `${bx - uy * wing},${by + ux * wing}`,
            `${bx + uy * wing},${by - ux * wing}`,
          ].join(" ");

          return (
            <g key={`${fromWord}-${toWord}`} style={{ transition: "opacity 300ms ease" }}>
              <line
                x1={p1.x}
                y1={p1.y}
                x2={bx}
                y2={by}
                stroke={stroke}
                strokeWidth={active ? 3 : 2}
                style={{ transition: "stroke 300ms ease" }}
              />
              <polygon
                points={head}
                fill={stroke}
                style={{ transition: "fill 300ms ease" }}
              />
            </g>
          );
        })}
        {nodes.map((n) => {
          const active = activeWord === n.word;
          // The conclusion's turn lifts the whole node — scale +
          // rise with a springy overshoot — where the others only
          // change stroke. transform-box makes the CSS transform
          // origin the node's own center rather than the SVG's.
          const lifted = active && n.word === CONCLUSION;
          return (
            <g
              key={n.word}
              style={{
                transformBox: "fill-box",
                transformOrigin: "center",
                transform: lifted ? "translateY(-6px) scale(1.1)" : "none",
                transition: "transform 420ms cubic-bezier(0.34, 1.56, 0.64, 1)",
              }}
            >
              <rect
                x={n.cx - n.w / 2}
                y={n.cy - n.h / 2}
                width={n.w}
                height={n.h}
                rx={10}
                fill={nodeFill}
                stroke={active ? activeColor : mutedStroke}
                strokeWidth={active ? 3.5 : 1.5}
                style={{
                  transition: "stroke 300ms ease, stroke-width 300ms ease, filter 300ms ease",
                  filter: lifted
                    ? `drop-shadow(0 8px 18px ${alpha(activeColor, 0.5)}) drop-shadow(0 0 10px ${alpha(activeColor, 0.4)})`
                    : active
                      ? `drop-shadow(0 0 10px ${alpha(activeColor, 0.55)})`
                      : "none",
                }}
              />
              <text
                x={n.cx}
                y={n.cy}
                textAnchor="middle"
                dominantBaseline="central"
                fill={active ? activeColor : textFill}
                style={{
                  fontFamily: "inherit",
                  fontSize: 16,
                  fontWeight: 600,
                  transition: "fill 300ms ease",
                }}
              >
                {n.word}
              </text>
            </g>
          );
        })}
      </svg>
    </Box>
  );
};
